import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { Sum } from '@sorare/core/src/hooks/useMonetaryAmount';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import type { ClickAddMultipleToCart_Origin } from '@sorare/core/src/protos/events/so5/shared/events';

import { useMarketplaceContext } from 'contexts/Marketplace';
import { useCart } from 'hooks/useCart';

import type { useAddMultipleCardsToCart_anyCard } from './__generated__/useAddMultipleCardsToCart.graphql';
import { useFilterLivePrimaryBuys } from './useFilterLivePrimaryBuys';

type Props<C extends useAddMultipleCardsToCart_anyCard> = {
  rarity: Rarity;
  cards: Nullable<C[]>;
  sport: Sport;
  origin: ClickAddMultipleToCart_Origin;
  watchlistId?: string;
};

export const useAddMultipleCardsToCart = <
  C extends useAddMultipleCardsToCart_anyCard,
>({
  rarity,
  cards,
  sport,
  origin,
  watchlistId,
}: Props<C>) => {
  const { showAddToCartPopin } = useMarketplaceContext();
  const { track } = useEventsContext();
  const { addCardsToCart, cart } = useCart();

  const cardsWithLivePrimaryOffer = useFilterLivePrimaryBuys(cards || []);

  const sortedMissingCards = cardsWithLivePrimaryOffer.sort((a, b) => {
    const aPrice = a.monetaryAmount.eurCents;
    const bPrice = b.monetaryAmount.eurCents;
    return aPrice - bPrice;
  });

  const totalPrice = Sum(sortedMissingCards.map(card => card.monetaryAmount));

  const trackAddMultipleToCart = (
    card_slugs: string[],
    offer_ids: string[],
    price: MonetaryAmount
  ) => {
    track('Click Add Multiple To Cart', {
      cartId: cart?.id,
      card_slugs,
      scarcity: rarity,
      offer_ids,
      eth_amount: price.eth,
      eur_amount: price.eur,
      origin,
      sport,
      watchlistId,
    });
  };

  const addMultipleCardsToCart = (() => {
    const missingSlugs = sortedMissingCards.map(({ card }) => card.slug);
    const offerIds = sortedMissingCards
      .map(({ card }) => card.livePrimaryOffer?.id)
      .filter(Boolean);
    return {
      mutate: async () => {
        trackAddMultipleToCart(missingSlugs, offerIds, totalPrice);
        const c = await addCardsToCart.mutate(sport, missingSlugs);
        const addedToCartSlugs =
          c?.mainCartItems?.map(item => item.card.slug) || [];
        showAddToCartPopin(addedToCartSlugs);
      },
      loading: addCardsToCart.loading,
    };
  })();

  return {
    addMultipleCardsToCart,
    totalPrice,
  };
};

useAddMultipleCardsToCart.fragments = {
  anyCard: gql`
    fragment useAddMultipleCardsToCart_anyCard on AnyCardInterface {
      slug
      rarityTyped
      anyTeam {
        slug
      }
      anyPlayer {
        slug
      }
      ...useFilterLivePrimaryBuys_anyCard
      ...useCart_anyCard
    }
    ${useFilterLivePrimaryBuys.fragments.anyCard}
    ${useCart.fragments.anyCard}
  ` as TypedDocumentNode<useAddMultipleCardsToCart_anyCard>,
};
